import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClub } from '../App';
import api from '../utils/api';
import {
  Users, Trophy, Swords, BarChart3, ChevronRight, CheckCircle2,
  LayoutDashboard, ArrowRight
} from 'lucide-react';

const TABS = [
  { id: 'overview', label: 'Overview', Icon: LayoutDashboard },
  { id: 'tournaments', label: 'Tournaments', Icon: Trophy },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const { selectedClub } = useClub();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!selectedClub?.id) return;
    const clubId = selectedClub.id;
    Promise.all([
      api.get(`/stats?group_id=${clubId}`),
      api.get(`/tournaments?group_id=${clubId}`)
    ])
      .then(([s, t]) => { setStats(s.data); setTournaments(t.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedClub?.id]);

  const canManage = selectedClub?.my_role === 'admin' || selectedClub?.my_role === 'contributor' || user?.role === 'admin';

  const statBoxes = [
    { label: 'Players', value: stats?.players ?? 0, Icon: Users, color: 'text-green-600', to: '/players', testid: 'stat-players' },
    { label: 'Tourns', value: stats?.total_tournaments ?? 0, Icon: Trophy, color: 'text-blue-600', to: '/tournaments', testid: 'stat-total-tournaments' },
    { label: 'Active', value: stats?.active_tournaments ?? 0, Icon: CheckCircle2, color: 'text-emerald-600', to: '/tournaments', testid: 'stat-active-tournaments' },
    { label: 'Games', value: stats?.completed_games ?? 0, Icon: Swords, color: 'text-slate-600', to: '/standings', testid: 'stat-games' },
  ];

  const recentTournaments = tournaments.slice(0, 5);

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      {/* Header */}
      <div>
        <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Hi, {user?.username}
        </h1>
        <p className="text-[11px] text-slate-500">{selectedClub?.name}</p>
      </div>

      {/* Stats row */}
      <div className="flex gap-1.5" data-testid="dashboard-stats">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex-1 bg-white rounded-lg border border-slate-200 p-2 animate-pulse h-14" />
          ))
        ) : (
          statBoxes.map(({ label, value, Icon, color, to, testid }) => (
            <button
              key={label}
              onClick={() => navigate(to)}
              className="flex-1 bg-white rounded-lg border border-slate-200 py-1.5 px-1 text-center hover:border-green-300 transition-all"
              data-testid={testid}
            >
              <Icon size={13} className={`${color} mx-auto mb-0.5`} />
              <p className="text-base font-bold text-slate-900 leading-none font-mono">{value}</p>
              <p className="text-[9px] text-slate-400 mt-0.5">{label}</p>
            </button>
          ))
        )}
      </div>

      {/* Tab strip */}
      <div className="flex border-b border-slate-200">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-all -mb-px ${
              activeTab === id
                ? 'border-green-500 text-green-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`dashboard-tab-${id}`}
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {activeTab === 'overview' && (
        <div className="space-y-3">
          {/* Quick nav */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { to: '/players', label: 'Players', Icon: Users, show: canManage },
              { to: '/tournaments', label: 'Tournaments', Icon: Trophy, show: true },
              { to: '/standings', label: 'Standings', Icon: BarChart3, show: true },
            ].filter(x => x.show).map(({ to, label, Icon }) => (
              <Link
                key={to}
                to={to}
                className="flex flex-col items-center gap-1 py-3 bg-white rounded-lg border border-slate-200 hover:border-green-300 transition-all"
                data-testid={`quick-nav-${label.toLowerCase()}`}
              >
                <Icon size={16} className="text-slate-500" />
                <span className="text-[10px] font-medium text-slate-600">{label}</span>
              </Link>
            ))}
          </div>

          {/* Recent tournaments */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Trophy size={13} className="text-green-600" />
                <span className="text-xs font-bold text-slate-900">Recent Tournaments</span>
              </div>
              <Link to="/tournaments" className="text-[10px] text-green-600 font-medium" data-testid="view-all-tournaments">
                View all
              </Link>
            </div>
            {loading ? (
              <div className="p-3 space-y-2">
                {[1,2,3].map(i => <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />)}
              </div>
            ) : recentTournaments.length === 0 ? (
              <div className="p-6 text-center">
                <Trophy className="mx-auto w-7 h-7 text-slate-300 mb-1.5" />
                <p className="text-xs text-slate-500">No tournaments yet</p>
                {canManage && (
                  <Link to="/tournaments" className="text-xs text-green-600 font-medium mt-1 inline-block">
                    Create one
                  </Link>
                )}
              </div>
            ) : (
              recentTournaments.map((t, i) => (
                <Link
                  key={t.id}
                  to={`/tournaments/${t.id}`}
                  className={`flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors ${i < recentTournaments.length - 1 ? 'border-b border-slate-100' : ''}`}
                  data-testid={`dashboard-tournament-${t.id}`}
                >
                  <div className="w-6 h-6 rounded-md bg-green-600 flex items-center justify-center flex-shrink-0">
                    <Trophy size={12} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate">{t.name}</p>
                    <p className="text-[10px] text-slate-500">{t.player_count}p · {t.game_count}g · {t.num_courts}c</p>
                  </div>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                    t.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
                  }`}>{t.status}</span>
                  <ChevronRight size={12} className="text-slate-300 flex-shrink-0" />
                </Link>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tournaments tab */}
      {activeTab === 'tournaments' && (
        <div className="space-y-2" data-testid="dashboard-tournaments-tab">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-slate-500">{tournaments.length} total</span>
            <Link to="/tournaments" className="text-[10px] text-green-600 font-medium">Manage →</Link>
          </div>
          {loading ? (
            [1,2,3].map(i => <div key={i} className="h-12 bg-white rounded-lg border border-slate-200 animate-pulse" />)
          ) : tournaments.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-6 text-center">
              <Trophy className="mx-auto w-7 h-7 text-slate-300 mb-1.5" />
              <p className="text-xs text-slate-500">No tournaments yet</p>
              {canManage && (
                <Link to="/tournaments" className="text-xs text-green-600 font-medium mt-1 inline-block">Create one</Link>
              )}
            </div>
          ) : (
            tournaments.map(t => (
              <Link
                key={t.id}
                to={`/tournaments/${t.id}`}
                className="flex items-center gap-2 px-3 py-2.5 bg-white rounded-lg border border-slate-200 hover:border-green-300 transition-all group"
                data-testid={`dash-tourn-${t.id}`}
              >
                <div className="w-7 h-7 rounded-md bg-green-50 flex items-center justify-center flex-shrink-0">
                  <Trophy size={14} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-900 truncate group-hover:text-green-700">{t.name}</p>
                  <p className="text-[10px] text-slate-500">{t.player_count}p · {t.game_count}g · {t.num_courts}c · {t.start_date}</p>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                  t.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
                }`}>{t.status}</span>
                <ArrowRight size={12} className="text-slate-300 group-hover:text-green-500 flex-shrink-0" />
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}
